import { RouteRecordRawArgumented } from 'src/types/route'
import useRouteParser from 'src/hooks/routeParser'
import useI18n from 'src/hooks/i18n'

const { t } = useI18n()
const {
  getPaginationInject,
  getPaginationCasts,
  toArrayOfInt,
  toBoolean,
  toInt,
} = useRouteParser()

const module: RouteRecordRawArgumented = {
  path: 'carbody',
  name: 'carBody',
  redirect: () => ({ name: 'carBodyIndex' }),
  children: [
    {
      path: 'prices',
      name: 'carBodyIndex',
      component: () =>
        import(
          /* webpackChunkName: "carbody-index" */ 'pages/carbody/IndexPage.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.index'),
          icon: 'mdi-land-rows-horizontal',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
        inject: {
          query: {
            ...getPaginationInject(),
            sort: 'created_at',
            dir: 'desc',
            available: true,
          },
        },
        casts: {
          query: {
            ...getPaginationCasts(),
            supplier_id: toArrayOfInt,
            available: toBoolean,
          },
        },
      },
      children: [
        {
          path: 'upload',
          name: 'carBodyUpload',
          component: () =>
            import(
              /* webpackChunkName: "carbody-upload" */ 'pages/carbody/UploadPage.vue'
            ),
          meta: {
            breadcrumb: {
              icon: 'mdi-upload',
              name: t('breadcrumb.carbody.upload'),
            },
            auth: {
              permission: 'car_body_prices_import',
            },
          },
        },
        {
          path: 'compare',
          name: 'carBodyCompare',
          component: () =>
            import(
              /* webpackChunkName: "carbody-compare" */ 'pages/carbody/ComparePage.vue'
            ),
          meta: {
            breadcrumb: {
              icon: 'mdi-compare',
              name: t('breadcrumb.carbody.compare'),
            },
            inject: {
              query: {
                ...getPaginationInject(),
                sort: 'name',
                dir: 'asc',
                available: true,
              },
            },
            auth: {
              permission: 'car_body_prices_index',
            },
            casts: {
              query: {
                ...getPaginationCasts(),
                model_id: toArrayOfInt,
                available: toBoolean,
              },
            },
          },
        },
      ],
    },
    {
      path: 'order/:session_id',
      name: 'carBodyOrder',
      component: () =>
        import(
          /* webpackChunkName: "carbody-order" */ 'pages/carbody/OrderPage.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.order'),
          icon: 'mdi-land-rows-horizontal',
        },
        auth: {
          permission: 'order_bot_car_body',
        },
        inject: {
          query: {
            ...getPaginationInject(),
            sort: 'code',
            dir: 'asc',
          },
        },
        casts: {
          params: {
            session_id: toInt,
          },
          query: {
            ...getPaginationCasts(),
          },
        },
      },
    },
    {
      path: 'create-new-product/:step?',
      name: 'carBodyCreateNewProduct',
      component: () =>
        import(
          /* webpackChunkName: "carbody-create-new-product" */ 'pages/carbody/CreateNewProduct.vue'
        ),
      meta: {
        breadcrumb: {
          icon: 'mdi-store-plus-outline',
          name: t('breadcrumb.carbody.create_new_product'),
        },
        inject: {
          query: {
            active: true,
          },
          params: {
            // 'list' = the create-product batch; 'review' = the pre-submit
            // review step (image_url + scaffold panels) that replaced the
            // old direct "SALVA TUTTI" button (2026-07-07 rework).
            step: 'list',
          },
        },
        auth: {
          permission: 'car_body_prices_index',
        },
        casts: {
          query: {
            active: toBoolean,
            // supplier_id and supplier_product_code are passed through from the
            // tmp-list row modal; supplier_id is cast to int for typed usage
            supplier_id: toInt,
          },
        },
      },
    },
    {
      path: 'updatable-products',
      name: 'carBodyUpdatableProducts',
      component: () =>
        import(
          /* webpackChunkName: "carbody-updatable-products" */ 'pages/carbody/UpdatableProducts.vue'
        ),
      meta: {
        breadcrumb: {
          icon: 'mdi-clipboard-edit-outline',
          name: t('breadcrumb.carbody.updatable_products'),
        },
        auth: {
          permission: 'car_body_prices_index',
        },
        inject: {
          query: {
            ...getPaginationInject(),
            sort: 'cati_code',
            dir: 'asc',
          },
        },
        casts: {
          query: {
            ...getPaginationCasts(),
          },
        },
      },
    },
    {
      path: 'tmp-products-index',
      name: 'carBodyTmpProductsIndex',
      component: () =>
        import(
          /* webpackChunkName: "carbody-index" */ 'pages/carbody/TmpProductsIndex.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.tmp_products_index'),
          icon: 'mdi-new-box',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
        inject: {
          query: {
            ...getPaginationInject(),
            sort: 'created_at',
            dir: 'desc',
          },
        },
        casts: {
          query: {
            ...getPaginationCasts(),
            supplier_id: toArrayOfInt,
            show_hidden: toBoolean,
          },
        },
      },
    },
    {
      path: 'pending-products',
      name: 'carBodyPendingProducts',
      component: () =>
        import(
          /* webpackChunkName: "carbody-pending-products" */ 'pages/carbody/PendingProducts.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.pending_products_index'),
          icon: 'mdi-truck-outline',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
        inject: {
          query: {
            ...getPaginationInject(),
            sort: 'created_at',
            dir: 'desc',
          },
        },
        casts: {
          query: {
            ...getPaginationCasts(),
          },
        },
      },
    },
    {
      path: 'export-suppliers',
      name: 'carBodyExportSuppliers',
      component: () =>
        import(
          /* webpackChunkName: "carbody-export-suppliers" */ 'pages/carbody/ExportSuppliers.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.export_suppliers'),
          icon: 'mdi-table-cog',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
      },
    },
    {
      path: 'cars-data',
      name: 'carBodyCarsData',
      component: () =>
        import(
          /* webpackChunkName: "carbody-cars-data" */ 'pages/carbody/CarsData.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.cars_data'),
          icon: 'mdi-car-multiple',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
        inject: {
          query: {
            ...getPaginationInject(),
            sort: 'brand',
            dir: 'asc',
          },
        },
        casts: {
          query: {
            ...getPaginationCasts(),
          },
        },
      },
    },
    // CATALOGUE ROW ORDER: the virtualised, block-grouped view of the
    // catalogue export's own rows (one per product x car), with drag-to-move
    // reordering inside a car block. Same `car_body_prices_index` gate as the
    // backend's five `catalogue-rows*` endpoints.
    {
      path: 'catalogue-order',
      name: 'carBodyCatalogueOrder',
      component: () =>
        import(
          /* webpackChunkName: "carbody-catalogue-order" */ 'pages/carbody/CatalogueOrderPage.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.catalogue_order'),
          icon: 'mdi-sort-variant',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
      },
    },
    // CATALOGUE MASTER SYNC (task 2026-08-28-014): upload the master catalogue
    // file, run a simulation or a write, poll progress, download reports. The
    // parent path is a redirect to History (Screen 3, the landing screen);
    // "Nuova sincronizzazione" is reached via a button there, gated on the
    // stricter `car_body_catalogue_sync` permission — a read-only operator
    // (only `car_body_prices_index`) can watch but not start/mutate a run.
    {
      path: 'catalogue-sync',
      name: 'carBodyCatalogueSync',
      redirect: () => ({ name: 'carBodyCatalogueSyncHistory' }),
      meta: {
        auth: {
          permission: 'car_body_prices_index',
        },
      },
    },
    {
      path: 'catalogue-sync/new',
      name: 'carBodyCatalogueSyncNew',
      component: () =>
        import(
          /* webpackChunkName: "carbody-catalogue-sync" */ 'pages/carbody/catalogue-sync/NewSyncPage.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.catalogue_sync_new'),
          icon: 'mdi-database-sync',
        },
        auth: {
          permission: 'car_body_catalogue_sync',
        },
      },
    },
    {
      path: 'catalogue-sync/runs/:uuid',
      name: 'carBodyCatalogueSyncRun',
      component: () =>
        import(
          /* webpackChunkName: "carbody-catalogue-sync" */ 'pages/carbody/catalogue-sync/RunStatusPage.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.catalogue_sync_run'),
          icon: 'mdi-database-sync',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
      },
    },
    {
      path: 'catalogue-sync/history',
      name: 'carBodyCatalogueSyncHistory',
      component: () =>
        import(
          /* webpackChunkName: "carbody-catalogue-sync" */ 'pages/carbody/catalogue-sync/HistoryPage.vue'
        ),
      meta: {
        breadcrumb: {
          name: t('breadcrumb.carbody.catalogue_sync_history'),
          icon: 'mdi-database-sync',
        },
        auth: {
          permission: 'car_body_prices_index',
        },
        inject: {
          query: {
            ...getPaginationInject(),
            sort: 'created_at',
            dir: 'desc',
          },
        },
        casts: {
          query: {
            ...getPaginationCasts(),
          },
        },
      },
    },
  ],
}

export default module
